import { FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BackButton from "../../components/BackButton";
import PageTitle from "../../components/PageTitle";
import LoadingEllipsis from "../../components/LoadingEllipsis";
import Ghost from "../../components/Ghost";
import NegativeButton from "../../components/NegativeButton";
import NeutralButton from "../../components/NeutralButton";
import PositiveButton from "../../components/PositiveButton";
import SimpleAlert from "../../components/SimpleAlert";
import FeaturePromotionStatusBadge from "../../components/FeaturePromotions/FeaturePromotionStatusBadge";
import useFeaturePromotionsService from "../../services/FeaturePromotionsService";
import {
  FeaturePromotion,
  featurePromotionStatus,
  parseUtc,
} from "../../types/FeaturePromotion";
import {
  FEATURE_KEY_PATTERN,
  KNOWN_FEATURE_KEYS,
  findKnownFeatureKey,
} from "../../constants/featurePromotions";
import { formatRelativeTime } from "../../utilities/DateUtilities";

const TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

/** A Date as the value a datetime-local input expects, in the browser's time zone. */
function toLocalInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

/** A datetime-local value, read in the browser's time zone. Null when empty or unreadable. */
function fromLocalInputValue(value: string): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
}

function formatUtc(date: Date | null): string {
  if (!date) return "";
  return date.toISOString().replace("T", " ").substring(0, 16) + " UTC";
}

interface FormState {
  id: string;
  featureKey: string;
  startsLocal: string;
  endsLocal: string;
  isEnabled: boolean;
  description: string;
}

function emptyForm(): FormState {
  const now = new Date();
  now.setSeconds(0, 0);
  const twoWeeks = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
  return {
    id: "",
    featureKey: "",
    startsLocal: toLocalInputValue(now),
    endsLocal: toLocalInputValue(twoWeeks),
    isEnabled: true,
    description: "",
  };
}

function toForm(p: FeaturePromotion): FormState {
  return {
    id: p.id ?? "",
    featureKey: p.featureKey,
    startsLocal: toLocalInputValue(parseUtc(p.startsAtUtc)),
    endsLocal: toLocalInputValue(parseUtc(p.endsAtUtc)),
    isEnabled: p.isEnabled,
    description: p.description ?? "",
  };
}

export default function AdminFeaturePromotionsDetailPage() {
  const navigate = useNavigate();
  const { promotionId } = useParams();
  const isNew = promotionId?.toLowerCase() === "new";
  const { getFeaturePromotion, saveFeaturePromotion, deleteFeaturePromotion } =
    useFeaturePromotionsService();

  const [isLoading, setIsLoading] = useState(!isNew);
  const [isSaving, setIsSaving] = useState(false);
  const [isClean, setIsClean] = useState(true);
  const [saved, setSaved] = useState<FeaturePromotion | null>(null);
  const [formData, setFormData] = useState<FormState>(emptyForm);
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [endNowAlertOpen, setEndNowAlertOpen] = useState(false);

  useEffect(() => {
    if (isNew || !promotionId) return;
    setIsLoading(true);
    getFeaturePromotion(promotionId)
      .then((p) => {
        setSaved(p);
        setFormData(toForm(p));
      })
      .catch((e: Error) => {
        console.error("Error fetching feature promotion:", e);
        setServerError(e.message);
      })
      .finally(() => setIsLoading(false));
    // Keyed on the id alone: the service functions change identity every render (the auth hook
    // underneath isn't memoised), and refetching mid-edit would throw away the admin's changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promotionId, isNew]);

  const starts = fromLocalInputValue(formData.startsLocal);
  const ends = fromLocalInputValue(formData.endsLocal);
  const known = findKnownFeatureKey(formData.featureKey.trim());

  const validate = (): boolean => {
    const errors: { [key: string]: string } = {};
    const key = formData.featureKey.trim();
    if (!key) {
      errors.featureKey = "Feature key is required.";
    } else if (!FEATURE_KEY_PATTERN.test(key)) {
      errors.featureKey =
        "Use 1-64 lower case letters, digits, '_', '-' or '.', e.g. high_quality_streaming.";
    }
    if (!starts) errors.startsLocal = "Start time is required.";
    if (!ends) errors.endsLocal = "End time is required.";
    if (starts && ends && ends <= starts) errors.endsLocal = "End must be after start.";
    if (formData.description.length > 500) {
      errors.description = "Keep the note to 500 characters or fewer.";
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const persist = async (form: FormState) => {
    setIsSaving(true);
    setServerError(null);
    try {
      const result = await saveFeaturePromotion({
        id: form.id || null,
        featureKey: form.featureKey.trim(),
        startsAtUtc: fromLocalInputValue(form.startsLocal)!.toISOString(),
        endsAtUtc: fromLocalInputValue(form.endsLocal)!.toISOString(),
        isEnabled: form.isEnabled,
        description: form.description.trim() || null,
      });
      setSaved(result);
      setFormData(toForm(result));
      setIsClean(true);
      if (isNew && result.id) {
        navigate(`/admin/featurepromotions/${result.id}`, { replace: true });
      }
    } catch (e) {
      setServerError((e as Error).message);
    } finally {
      setIsSaving(false);
    }
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    await persist(formData);
  };

  /** Ends a live promotion at this minute but keeps its record, unlike delete. */
  const endNow = async () => {
    // SimpleAlert leaves closing to its caller.
    setEndNowAlertOpen(false);
    const now = new Date();
    now.setSeconds(0, 0);
    // The minute may still be the start minute; end is exclusive, so step past it.
    if (starts && now <= starts) now.setTime(starts.getTime() + 60 * 1000);
    await persist({ ...formData, endsLocal: toLocalInputValue(now) });
  };

  const deleteThis = async () => {
    if (!formData.id) return;
    setDeleteAlertOpen(false);
    setIsSaving(true);
    try {
      await deleteFeaturePromotion(formData.id);
      navigate("/admin/featurepromotions");
    } catch (e) {
      setServerError((e as Error).message);
      setIsSaving(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setIsClean(false);
    setFormData({ ...formData, [name]: value });
  };

  const fieldError = (name: string) =>
    validationErrors[name] ? (
      <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
        {validationErrors[name]}
      </div>
    ) : null;

  const invalidStyle = (name: string) => (validationErrors[name] ? "border-red-800" : "");

  const savedStatus = saved ? featurePromotionStatus(saved) : null;

  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-row items-center gap-4">
        <BackButton />
        <PageTitle className="text-4xl">
          {isLoading
            ? "Loading"
            : isNew
            ? "New feature promotion"
            : findKnownFeatureKey(saved?.featureKey ?? "")?.name ?? saved?.featureKey}
          {isLoading && <LoadingEllipsis />}
        </PageTitle>
        {savedStatus && <FeaturePromotionStatusBadge status={savedStatus} />}
      </div>

      {saved && savedStatus === "live" && (
        <p>
          Live in the app now. Ends {formatRelativeTime(parseUtc(saved.endsAtUtc))} (
          {parseUtc(saved.endsAtUtc).toLocaleString("en-GB")}).
        </p>
      )}
      {saved && savedStatus === "scheduled" && (
        <p>Starts {formatRelativeTime(parseUtc(saved.startsAtUtc))}.</p>
      )}

      {serverError && (
        <div className="text-white bg-red-800 py-2 px-4 rounded-xl">{serverError}</div>
      )}

      {isLoading && <Ghost additionalClasses="h-24 rounded" />}

      {!isLoading && (
        <form onSubmit={save} className="form">
          <div className="grid grid-cols-1 gap-x-6 gap-y-8 xl:grid-cols-2">
            <div className="card p-8 flex flex-col gap-4">
              <div>
                <label htmlFor="featureKey" className="block leading-6">
                  Feature key *
                </label>
                <input
                  disabled={isSaving}
                  id="featureKey"
                  type="text"
                  name="featureKey"
                  list="known-feature-keys"
                  autoComplete="off"
                  placeholder="high_quality_streaming"
                  value={formData.featureKey}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded text-black font-mono ${invalidStyle(
                    "featureKey"
                  )}`}
                />
                <datalist id="known-feature-keys">
                  {KNOWN_FEATURE_KEYS.map((k) => (
                    <option key={k.key} value={k.key}>
                      {k.name}
                    </option>
                  ))}
                </datalist>
                {fieldError("featureKey")}
                {known && <p className="text-sm mt-2 opacity-75">{known.description}</p>}
                {!known && formData.featureKey.trim() && !validationErrors.featureKey && (
                  <p className="text-sm mt-2 text-amber-400">
                    Not a key the portal knows about. That's fine if a new app build honours it —
                    builds that don't recognise a key ignore it.
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="description" className="block leading-6">
                  Note (internal)
                </label>
                <textarea
                  disabled={isSaving}
                  id="description"
                  name="description"
                  rows={3}
                  placeholder="e.g. Launch week: HQ streaming free for everyone"
                  value={formData.description}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded text-black ${invalidStyle(
                    "description"
                  )}`}
                />
                {fieldError("description")}
              </div>

              <label className="flex items-center gap-2">
                <input
                  disabled={isSaving}
                  type="checkbox"
                  checked={formData.isEnabled}
                  onChange={(e) => {
                    setIsClean(false);
                    setFormData({ ...formData, isEnabled: e.target.checked });
                  }}
                />
                Enabled
              </label>
            </div>

            <div className="card p-8 flex flex-col gap-4">
              <p className="text-sm opacity-75">
                Times are in your time zone ({TIME_ZONE}) and stored in UTC.
              </p>
              <div>
                <label htmlFor="startsLocal" className="block leading-6">
                  Starts *
                </label>
                <input
                  disabled={isSaving}
                  id="startsLocal"
                  type="datetime-local"
                  name="startsLocal"
                  value={formData.startsLocal}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded text-black ${invalidStyle(
                    "startsLocal"
                  )}`}
                />
                <p className="text-xs mt-1 opacity-75">{formatUtc(starts)}</p>
                {fieldError("startsLocal")}
              </div>

              <div>
                <label htmlFor="endsLocal" className="block leading-6">
                  Ends *
                </label>
                <input
                  disabled={isSaving}
                  id="endsLocal"
                  type="datetime-local"
                  name="endsLocal"
                  value={formData.endsLocal}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded text-black ${invalidStyle(
                    "endsLocal"
                  )}`}
                />
                <p className="text-xs mt-1 opacity-75">{formatUtc(ends)}</p>
                {fieldError("endsLocal")}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 justify-end mt-4">
            {formData.id && (
              <NegativeButton
                type="button"
                disabled={isSaving}
                onClick={() => setDeleteAlertOpen(true)}
              >
                Delete
              </NegativeButton>
            )}
            {formData.id && savedStatus === "live" && (
              <NeutralButton
                type="button"
                disabled={isSaving}
                onClick={() => setEndNowAlertOpen(true)}
              >
                End now
              </NeutralButton>
            )}
            <PositiveButton type="submit" disabled={isSaving || isClean}>
              {isSaving ? "Saving..." : formData.id && isClean ? "Saved" : "Save"}
            </PositiveButton>
          </div>
        </form>
      )}

      <SimpleAlert
        open={endNowAlertOpen}
        setOpen={setEndNowAlertOpen}
        title="End this promotion now?"
        message="The end time is set to now and saved. Apps stop honouring it within about a minute; streams already live keep what they started with."
        confirmText="End now"
        onConfirm={endNow}
      />
      <SimpleAlert
        open={deleteAlertOpen}
        setOpen={setDeleteAlertOpen}
        title="Delete this promotion?"
        message="This removes it and its history. To stop it but keep the record, use End now or untick Enabled instead."
        confirmText="Delete"
        onConfirm={deleteThis}
      />
    </section>
  );
}
